import { useEffect, useState } from 'react'
import { useTheme } from '@mui/material/styles'

export const ASSISTANT_PANEL_WIDTH = 480
export const ASSISTANT_WINDOW_HEIGHT = 640
export const ASSISTANT_WINDOW_OFFSET = 24
export const ASSISTANT_MOTION_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)'
export const ASSISTANT_OPEN_MS = 250
export const ASSISTANT_CLOSE_MS = 150
export const ASSISTANT_SCALE = 0.96

/**
 * Detail previews are right-anchored side modals (preview_id) whose backdrop
 * sits above the panel — measure the modal so the assistant can dock to its
 * left and stay clickable above the backdrop.
 */
export function useSideModalDock(search: string, isExpanded: boolean) {
	const theme = useTheme()
	const sideModalOpen = new URLSearchParams(search).has('preview_id')
	const [sideModalWidth, setSideModalWidth] = useState(0)

	useEffect(() => {
		if (!sideModalOpen) {
			setSideModalWidth(0)
			return
		}
		const measure = () => {
			let width = 0
			document.querySelectorAll<HTMLElement>('.MuiDialog-paper').forEach((paper) => {
				width = Math.max(width, paper.getBoundingClientRect().width)
			})
			setSideModalWidth(width)
		}
		measure()
		// The dialog mounts and slides in async — re-measure after it settles.
		const timers = [window.setTimeout(measure, 150), window.setTimeout(measure, 450)]
		window.addEventListener('resize', measure)
		return () => {
			timers.forEach((t) => {
				window.clearTimeout(t)
			})
			window.removeEventListener('resize', measure)
		}
	}, [sideModalOpen])

	// Dock immediately left of an open side modal (clamped on narrow
	// viewports) and lift above its backdrop so the panel stays usable.
	const viewportW = typeof window === 'undefined' ? 1440 : window.innerWidth
	const panelWidthPx = isExpanded ? Math.min(800, viewportW - ASSISTANT_WINDOW_OFFSET * 2) : ASSISTANT_PANEL_WIDTH
	const dockedRight = sideModalWidth
		? Math.min(
				sideModalWidth + ASSISTANT_WINDOW_OFFSET,
				Math.max(ASSISTANT_WINDOW_OFFSET, viewportW - panelWidthPx - ASSISTANT_WINDOW_OFFSET)
			)
		: ASSISTANT_WINDOW_OFFSET
	const panelZIndex = sideModalWidth ? theme.zIndex.modal + 10 : theme.zIndex.drawer

	return { dockedRight, panelZIndex, sideModalDocked: sideModalWidth > 0 }
}
